import React, { useState } from 'react';
import {
  loadGamesModifiedByWeekByUser,
  exportGamesModifiedByWeekByUser,
} from '../../../../actions/reportByWeekActions';
import { Table, SimpleTextFilter, TableColumn, getVisibleColumns } from '../../../common/Table';
import { CommaNumber_NoDecimal, range } from '../../../../util/formatter';
import Loading from '../../../common/Loading';
import KwPanel from '../../../common/KwPanel';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../../hooks/useTableSettings';
import YearFormSelect from '../YearFormSelect';
import UserLink from '../../../common/links/UserLink';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export interface GamesModifiedByWeekByUserListProps { tableViewSwitcher?: React.ReactNode }
function GamesModifiedByWeekByUserList({ tableViewSwitcher }: GamesModifiedByWeekByUserListProps) {
  const { t } = useTranslation();
  const [reload, setReload] = useState<Date | undefined>();
  const location = useLocation();
  const allColumns = ['fullName', 'year'];
  for (let i = 1; i <= 52; i++) {
    allColumns.push(`week${i}`);
  }
  const defaultVisibleColumns = allColumns;
  const [items, setItems] = useState<any>();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useLocalStorage('report-year', currentYear);
  const years = range(currentYear, currentYear - 5, -1);
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
    parameters,
  } = useTableSettings(
    'gamesmodifiedbyweekbyuserlist-settings',
    getTableSettingsDefaults({
      defaultSort: 'fullName',
      defaultVisibleColumns,
    }),
    { year },
    loadGamesModifiedByWeekByUser,
    setItems,
    reload,
    t('Failed to load report')
  );

  // The base config of the columns, this will be filtered based on the visible (above)
  let columnsConfig: TableColumn[] = [
    {
      Key: 'fullName',
      Label: t('Uploading user'),
      Render: (item) => (
        <UserLink id={item.userProfileId} name={item.fullName} imageSize={32} />
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'year',
      Label: t('Year'),
      Render: (item) => item.year,
    },
  ];
  // add the remaining 52 weeks to the columnsConfig array
  for (let i = 1; i <= 52; i++) {
    columnsConfig.push({
      Key: `week${i}`,
      Label: i.toString(),
      Render: (row) => CommaNumber_NoDecimal(row[`week${i}`]),
    });
  }

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = Number(e.target.value);
    setYear(newYear);
    parameters.current = { ...parameters.current, year: newYear };
    setReload(new Date());
  };

  // return an alaska table
  return items ? (
    <>
      <div className="form-horizontal row px-4 px-md-0">
        <div className="col-md-6">
          <YearFormSelect
            year={year}
            years={years}
            handleYearChange={handleYearChange}
          ></YearFormSelect>
        </div>
      </div>
      <KwPanel className="mb-4">
        <Table
          className="table-v-stripes"
          columns={columns}
          allColumns={allColumns}
          rows={items.data.map((x: any) => {
            return { Key: x.uploadinguserId, ...x };
          })}
          totalRowCount={items.totalRowCount}
          filteredRowCount={items.filteredRowCount}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          isLoading={isLoading}
          filters={filters}
          filterChange={filterChange}
          exportData={(filters, scopes) =>
            exportGamesModifiedByWeekByUser(year, filters, scopes)
          }
          tableActionBarButtons={tableViewSwitcher}
        />
      </KwPanel>
    </>
  ) : (
    <Loading></Loading>
  );
}
export default GamesModifiedByWeekByUserList;
